
import {chain, forEach, includes, isEmpty, isUndefined, keys, map, size, sortBy, uniq, values, without} from 'lodash';
import {
    URL_FARE_CHANGES_SUMMARY_ATHENA_FLATTENED,
    URL_FARE_CHANGES_SUMMARY_MONGO_FLATTENED,
    URL_RECOMM_SUMMARY_FLATTENED,
    URL_SAVE_CRITERIA
} from '../../../end-points.js';
import {DateUtil} from '../../../utils/date-util.js';
import {queryBuilder} from '../../../utils/query-builder-util.js';
import {requestBuilder} from '../../../utils/request-builder-util.js';
import {getClonedObject, groupByMultipleProperties} from '../../../utils/common-util.js';
import {
    CRITERIA_MARKET_KEY,
    DASHBOARD_TOP_TABS_VALUE,
    PERCENTAGE_ABSOLUTE_TABS_VALUE
} from '../../../utils/constants.js';
import {getFareType} from '../app-configs-util/app-configs-util.js';

export const KEY_ANALYST = 'analyst';

export const DASHBOARD_GRAPH_X_AXIS_KEY = 'date';

export const DASHBOARD_STRUCTURAL_APPROVAL_STATUS_NO_RECOMMENDATION = 'No Recommendation';

export const DASHBOARD_TACTICAL_COMPETITOR_OTHER_FARE_COMPONENT_CHANGE = 'Competitor Other Fare Components Change';

export const DASHBOARD_TACTICAL_HOST_OTHER_FARE_COMPONENT_CHANGE = 'Host Other Fare Components Change';

export const UNIQUE_COUNT_KEY = 'Unique';

export const IMPACTED_FARES_COUNT_KEY = 'Impacted Fares';

export const RECOMMENDATION_SUMMARY_DATE_DIFF_THRESHOLD = 30;

export const COMPETITION_SUMMARY_DATE_DIFF_THRESHOLD = 15;

const BASE_FARE_CHANGES = 'baseFareChanges';
const YQ_CHANGES = 'yqChanges';
const YR_CHANGES = 'yrChanges';
const OTHER_TAX_CHANGES = 'otherTaxChanges';
const TOTAL_FARE_CHANGES = 'totalFareChanges';

export const BASE_FARE = 'baseFare';
const YQ = 'yq';
const YR = 'yr';
const OTHER_TAX = 'otherTax';
const TOTAL_FARE = 'totalFare';
export const DEFAULT_FARE_TYPE = getFareType('RT');
export const NO_OF_CHANGED_FARES_KEY = 'noOfChangedFares';

export const FARE_COMPONENTS = {
    [BASE_FARE]: 'Base Fare',
    [YQ]: 'YQ',
    [YR]: 'YR',
    [OTHER_TAX]: 'Other Tax',
    [TOTAL_FARE]: 'Total Fare'
}

const FARE_COMPONENTS_CHANGES = {
    [BASE_FARE_CHANGES]: 'Base Fare Changes',
    [YQ_CHANGES]: 'YQ Changes',
    [YR_CHANGES]: 'YR Changes',
    [OTHER_TAX_CHANGES]: 'Other Tax Changes',
    [TOTAL_FARE_CHANGES]: 'Total Fare Changes'
}

export const RECOMMENDATION_STATUS_KEYS = {
    RECOMMENDATION: 'aggregationByRecomReason',
    NO_RECOMMENDATION: 'aggregationByNoRecomReason',
    AUTO_APPROVAL_STOP: 'aggregationByAutoApprovalStopReason',
    APPROVAL_STATUS: 'aggregationByAnalyst'
};

export const RECOMMENDATION_STATUS_TABS = {
    [RECOMMENDATION_STATUS_KEYS.RECOMMENDATION]: {KEY: RECOMMENDATION_STATUS_KEYS.RECOMMENDATION, LABEL_TEXT: 'Recommendation'},
    [RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION]: {KEY: RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION, LABEL_TEXT: 'No Recommendation'},
    [RECOMMENDATION_STATUS_KEYS.AUTO_APPROVAL_STOP]: {KEY: RECOMMENDATION_STATUS_KEYS.AUTO_APPROVAL_STOP, LABEL_TEXT: 'Auto Approval Stop'},
    [RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS]: {KEY: RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS, LABEL_TEXT: 'Approval Status'}
};

export const COMPETITION_TAB_KEYS = {
    FARES: 'aggregationByFares'
};

export const COMPETITION_TABS = {
    FARES: {KEY: COMPETITION_TAB_KEYS.FARES, LABEL_TEXT: 'Fares'}
};

export const AGGREGATION_DATES = {
    AGGREGATION_FROM_DATE: 'aggregationFromDate',
    AGGREGATION_TO_DATE: 'aggregationToDate'
};

export const DASHBOARD_SEARCH_CRITERIA_KEYS = {
    CRITERIA_CATEGORY: 'criteriaCategory',
    CRITERIA_LOCATION: 'location',
    CRITERIA_MARKET: CRITERIA_MARKET_KEY,
    CRITERIA_MARKET_TIER: 'marketTier',
    CRITERIA_CABIN_CODE: 'cabinCode',
    CRITERIA_BENCHMARK_CARRIER: 'carrierCode',
    CRITERIA_STATUS: 'status',
    CRITERIA_FARE_TYPE: 'tripType',
    CRITERIA_MIN_MAX_GROUP: 'minMaxGroup',
    CRITERIA_ADVANCE_PURCHASE_GROUP: 'advancePurchaseGroup',
    CRITERIA_POS: 'pos',
    CRITERIA_RBD: 'rbd',
    CRITERIA_FARE_BRAND: 'fareBrand',
    CRITERIA_ANALYST: `${RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS}.${KEY_ANALYST}`,
    CRITERIA_MARKET_ORIGIN: 'origin',
    CRITERIA_MARKET_DESTINATION: 'destination',
    CRITERIA_MIN_GROUP: 'minStayGroup',
    CRITERIA_MAX_GROUP: 'maxStayGroup',
    CRITERIA_REGION: 'region',
    CRITERIA_AGGREGATION_DATE: 'aggregationDate',
    CRITERIA_TARIFF_NUMBER: 'tariffNumber',
    CRITERIA_RULE_NUMBER: 'ruleNumber',
    CRITERIA_FOOTNOTE_CODE: 'footnoteCode',
    CRITERIA_FARE_COMPONENT: 'fareComponent',
    CRITERIA_AGGREGATION_TIMESTAMP: 'aggregationTimestamp',
    CRITERIA_SEASON: 'season'
};

export const RECOMMENDATION_STATUS_KEYS_UNIQUE_COUNT_KEYS_MAPPING = {
    [RECOMMENDATION_STATUS_KEYS.RECOMMENDATION]: 'uniqueNewLadders',
    [RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION]: 'uniqueNoRecomLadders',
    [RECOMMENDATION_STATUS_KEYS.AUTO_APPROVAL_STOP]: 'uniqueLaddersStoppedFromAutoApproval'
};

export const RECOMMENDATION_STATUS_KEYS_IMPACTED_FARES_KEYS_MAPPING = {
    [RECOMMENDATION_STATUS_KEYS.RECOMMENDATION]: 'newLadderImpactedFares',
    [RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION]: 'noRecomLadderImpactedFares',
    [RECOMMENDATION_STATUS_KEYS.AUTO_APPROVAL_STOP]: 'stoppedFromAutoApprovalLadderImpactedFares'
};

export const RECOMMENDATION_RESPONSE_COUNT_KEYS = {
    GROUP_COUNT: 'groupCount',
    COUNT: 'count'
};

export const DAILY_WEEKLY_TOGGLE_TAB = {
    DAILY: 'd',
    WEEKLY: 'w',
};

export const PAYLOAD_PARAMS_DASHBOARD_SUMMARY = [
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_ANALYST
];

export const PAYLOAD_PARAMS_DASHBOARD_COMPETITION_SUMMARY = [
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_BENCHMARK_CARRIER,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT,
    DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON
];

const updateFiltersRawDataForOtherParams = (filtersRawData, eachParam, eachRecomm) => {
    filtersRawData[eachParam] ?
        (filtersRawData[eachParam].indexOf(eachRecomm[eachParam]) === -1 ? filtersRawData[eachParam].push(eachRecomm[eachParam]) : null) :
        (filtersRawData[eachParam] = [eachRecomm[eachParam]]);
}

const updateFiltersRawDataIfRecommendation = (eachRecomm, filtersRawData) => {
    map(PAYLOAD_PARAMS_DASHBOARD_SUMMARY, eachParam => {
        if (eachParam === DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_ANALYST) {
            map(eachRecomm[RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS], eachAnalyst => {
                filtersRawData[KEY_ANALYST] = uniq(filtersRawData[KEY_ANALYST] ? [...filtersRawData[KEY_ANALYST], ...eachAnalyst[KEY_ANALYST].split(',')] : eachAnalyst[KEY_ANALYST].split(','));
            });
        } else if (eachRecomm[eachParam]) {
            updateFiltersRawDataForOtherParams(filtersRawData, eachParam, eachRecomm);
        }
    });
}

const updateFiltersRawDataIfCompetition = (eachRecomm, filtersRawData) => {
    map(PAYLOAD_PARAMS_DASHBOARD_COMPETITION_SUMMARY, eachParam => {
        if (eachParam === DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER || eachParam === DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER || DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE === eachParam) {
            map(FARE_COMPONENTS_CHANGES, (eachFareComponentValue, eachFareComponentKey) => {
                eachRecomm[eachFareComponentKey] && map(eachRecomm[eachFareComponentKey].fares, eachFare => {
                    updateFiltersRawDataForOtherParams(filtersRawData, eachParam, eachFare);
                });
            });
        } else if (eachParam === DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT) {
            map(FARE_COMPONENTS, (eachFareComponentValue, eachFareComponentKey) => {
                if (eachRecomm[eachFareComponentKey]) {
                    filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT] = uniq(filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT] ? [...filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT], ...[eachFareComponentKey]] :  [eachFareComponentKey]);
                }
            });
        } else if (eachRecomm[eachParam]) {
            updateFiltersRawDataForOtherParams(filtersRawData, eachParam, eachRecomm);
        }
    });
}

export const getFiltersRawData = (summaryData, fromTo, recommCompetitor) => {
    const filtersRawData = {
        [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE]: {
            [AGGREGATION_DATES.AGGREGATION_FROM_DATE]: DateUtil.formatDate(fromTo[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE][AGGREGATION_DATES.AGGREGATION_FROM_DATE]),
            [AGGREGATION_DATES.AGGREGATION_TO_DATE]: DateUtil.formatDate(fromTo[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE][AGGREGATION_DATES.AGGREGATION_TO_DATE])
        }
    };
    map(summaryData, eachRecomm => {
        const originDestination = `${eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN]}-${eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION]}`;
        filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET] ?
            (filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET].indexOf(originDestination) === -1 ? filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET].push(originDestination) : null) :
            (filtersRawData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET] = [originDestination]);
        (DASHBOARD_TOP_TABS_VALUE.COMPETITION === recommCompetitor) ? updateFiltersRawDataIfCompetition(eachRecomm, filtersRawData) : updateFiltersRawDataIfRecommendation(eachRecomm, filtersRawData);
    });

    return filtersRawData;
};

export const computeData = (filteredSummaryData, recommendationStatus, datesInSequence, dailyWeeklyViewValue, filterBy) => {
    let computedChartData = getClonedObject(isEmpty(filterBy) ? datesInSequence : generateDatesBetween(getFromToDate(filterBy)));
    map(filteredSummaryData, eachRecomm => {
        const key = DateUtil.formatDate(eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE], DateUtil.DATE_FORMATS.YEAR_MONTH_DAY);
        computedChartData[key] = computedChartData[key] ? computeGroupCountForEachRecomm(eachRecomm, computedChartData[key], key, recommendationStatus) : {};
    });

    const dailyComputedChartData = chain(computedChartData)
        .filter(eachDate => !isUndefined(eachDate))
        .values()
        .sortBy(DASHBOARD_GRAPH_X_AXIS_KEY)
        .value();

    return DAILY_WEEKLY_TOGGLE_TAB.DAILY === dailyWeeklyViewValue ? dailyComputedChartData : checkIfWeeklyAndGetWeeklyComputedChartData(dailyWeeklyViewValue, computedChartData);
};

export const generateDatesBetween = (fromTo, isArrayForm) => {
    const computedChartDataWithDateAsKey = {};
    const startDateMoment = DateUtil.getNowMoment(fromTo[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE][AGGREGATION_DATES.AGGREGATION_FROM_DATE]);
    const endDateMoment = DateUtil.getNowMoment(fromTo[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE][AGGREGATION_DATES.AGGREGATION_TO_DATE]);
    const now = DateUtil.getClonnedNowMoment(startDateMoment);
    while (DateUtil.isSameMoment(now, endDateMoment) || DateUtil.isBeforeMoment(now, endDateMoment)) {
        computedChartDataWithDateAsKey[now.format(DateUtil.DATE_FORMATS.YEAR_MONTH_DAY)] = isArrayForm ? [] : {[DASHBOARD_GRAPH_X_AXIS_KEY]: now.format(DateUtil.DATE_FORMATS.YEAR_MONTH_DAY)};
        DateUtil.addDaysInMoment(now, 1);
    }

    return computedChartDataWithDateAsKey;
};

export const getAggregationDateOrTimeStampString = (eachRecomm, recommCompetitor, format) => {
    let aggregationDateOrTimeStamp = '';
    if (DASHBOARD_TOP_TABS_VALUE.COMPETITION === recommCompetitor) {
        aggregationDateOrTimeStamp = eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_TIMESTAMP].length === 6 ?
            eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_TIMESTAMP].match(/.{2}/g).join('-') :
            eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_TIMESTAMP];
    } else {
        aggregationDateOrTimeStamp = eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE];
    }

    return DateUtil.formatDate(aggregationDateOrTimeStamp, format);
}

export const getFilteredSummaryData = (summaryData, filterBy, recommendationStatus, recommCompetitor, onChangeFilterName = '') => {
    if (isEmpty(filterBy) || isEmpty(summaryData)) {
        return summaryData;
    }
    const copyOfSummaryData = getClonedObject(summaryData);
    let filteredSummaryData = [];
    const availableDates = generateDatesBetween(filterBy, true);

    map(copyOfSummaryData, eachRecomm => {
        const eachRecordDate = getAggregationDateOrTimeStampString(eachRecomm, recommCompetitor, DateUtil.DATE_FORMATS.YEAR_MONTH_DAY);
        if (checkIfCommonFiltersPresent(availableDates, eachRecordDate, filterBy, eachRecomm, onChangeFilterName)
            && checkIfRecommendationSpecificFiltersPresent(filterBy, eachRecomm, recommCompetitor, recommendationStatus, onChangeFilterName)
            && checkIfCompetitionSpecificFiltersPresent(filterBy, eachRecomm, recommCompetitor, onChangeFilterName)
        ) {
            // TODO: HAVE DATE AS KEY AND THEN RETURN THE VALUES
            filteredSummaryData.push(eachRecomm);
        }
    });

    return filteredSummaryData;
};

const checkIfCommonFiltersPresent = (availableDates, dateToValidate, filterBy, eachRecomm, onChangeFilterName) => {

    switch(onChangeFilterName) {
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE:
            return isDateWithinRange(availableDates, dateToValidate);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET:
            return isFoundMarket(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN:
            return isFoundMarketOrigin(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION:
            return isFoundMarketDestination(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE:
            return isFoundCabinCode(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS:
            return isFoundPos(filterBy, eachRecomm);
        default:
            return isDateWithinRange(availableDates, dateToValidate)
                && isFoundMarket(filterBy, eachRecomm)
                && isFoundMarketOrigin(filterBy, eachRecomm)
                && isFoundMarketDestination(filterBy, eachRecomm)
                && isFoundCabinCode(filterBy, eachRecomm)
                && isFoundPos(filterBy, eachRecomm);
    }
};

const checkIfRecommendationSpecificFiltersPresent = (filterBy, eachRecomm, recommCompetitor, recommendationStatus, onChangeFilterName) => {
    if (recommCompetitor && DASHBOARD_TOP_TABS_VALUE.RECOMMENDATION !== recommCompetitor) {
        return true;
    }

    switch(onChangeFilterName) {
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION:
            return isFoundRegion(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER:
            return isFoundMarketTier(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_ANALYST:
            return isFoundAnalyst(filterBy, eachRecomm, recommendationStatus);
        default:
            return isFoundRegion(filterBy, eachRecomm)
                && isFoundMarketTier(filterBy, eachRecomm)
                && isFoundAnalyst(filterBy, eachRecomm, recommendationStatus);
    }
}

const checkIfCompetitionSpecificFiltersPresent = (filterBy, eachRecomm, recommCompetitor, onChangeFilterName) => {
    if (recommCompetitor && DASHBOARD_TOP_TABS_VALUE.COMPETITION !== recommCompetitor) {
        return true;
    }

    switch(onChangeFilterName) {
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD:
            return isFoundRbd(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_BENCHMARK_CARRIER:
            return isFoundBenchmarkCarrier(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE:
            return isFoundFareType(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON:
            return isFoundSeason(filterBy, eachRecomm);
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER:
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE:
        case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER:
            return isFoundTariffFootnoteAndRuleNumber(filterBy, eachRecomm, onChangeFilterName);
        default:
            return isFoundRbd(filterBy, eachRecomm)
                && isFoundBenchmarkCarrier(filterBy, eachRecomm)
                && isFoundFareType(filterBy, eachRecomm)
                && isFoundSeason(filterBy, eachRecomm)
                && isFoundTariffFootnoteAndRuleNumber(filterBy, eachRecomm, onChangeFilterName);
    }
};

const isDateWithinRange = (availableDates, dateToValidate) => {
    return !isUndefined(availableDates[dateToValidate]);
};

const isFoundRecord = (whiteListedValues, compareValue) => {
    return whiteListedValues.indexOf(compareValue) > -1;
};

const isFoundMarket = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET], `${eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN]}-${eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION]}`);
};

const isFoundMarketOrigin = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN]);
};

const isFoundMarketDestination = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION]);
};

const isFoundCabinCode = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE]);
};

const isFoundPos = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS]);
};

const isFoundRegion = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION]);
};

const isFoundMarketTier = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER]);
};

const isFoundAnalyst = (filterBy, eachSummaryData, recommendationStatus) => {
    if (RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS !== recommendationStatus) {
        return true;
    }
    const analystDataList = [];
    map(eachSummaryData[RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS], analystData => {
        if (analystData && analystData[KEY_ANALYST] && filterBy && filterBy[KEY_ANALYST] && filterBy[KEY_ANALYST].indexOf(analystData[KEY_ANALYST]) > -1) {
            analystDataList.push(analystData);
        }
    });
    eachSummaryData[RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS] = analystDataList;

    return !isEmpty(analystDataList);
};

const isFoundRbd = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD]);
};

const isFoundBenchmarkCarrier = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_BENCHMARK_CARRIER], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_BENCHMARK_CARRIER]);
};

const isFoundFareType = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE]);
};

const isFoundSeason = (filterBy, eachRecomm) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON], eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON]);
};

const isFoundTariffNumber = (filterBy, fareData) => {
    return fareData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER] && isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER], fareData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER]);
};

const isFoundRuleNumber = (filterBy, fareData) => {
    return fareData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER] && isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER], fareData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER]);
};

const isFoundFootnoteCode = (filterBy, fareData) => {
    return isFoundRecord(filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE], fareData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE]);
};

const isFoundTariffFootnoteAndRuleNumber = (filterBy, eachSummaryData, onChangeFilterName) => {
    const fareChangeList = [];
    const fareComponentKey = `${filterBy[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT]}Changes`;
    const faresChanges = eachSummaryData[fareComponentKey];

    if (faresChanges) {
        map(faresChanges.fares, fareData => {
            if (fareData) {
                if ((DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER === onChangeFilterName && isFoundTariffNumber(filterBy, fareData))
                    || (DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER === onChangeFilterName && isFoundRuleNumber(filterBy, fareData))
                    || (DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE === onChangeFilterName && isFoundFootnoteCode(filterBy, fareData))
                    || (isFoundTariffNumber(filterBy, fareData) && isFoundRuleNumber(filterBy, fareData) && isFoundFootnoteCode(filterBy, fareData))) {

                    fareChangeList.push(fareData);
                }
            }
        });
        eachSummaryData[fareComponentKey].fares = fareChangeList;
        eachSummaryData[fareComponentKey][NO_OF_CHANGED_FARES_KEY] = size(fareChangeList);
    }

    return !isEmpty(fareChangeList);
};

const computeGroupCountForEachRecomm = (eachRecomm, computedChartEachDayValueObj, key, recommendationStatus) => {
    let computedChartEachDayValue = computedChartEachDayValueObj;
    if (recommendationStatus === RECOMMENDATION_STATUS_KEYS.RECOMMENDATION || recommendationStatus === RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION) {
        computedChartEachDayValue = computeGroupCountForEachRecommForRecommAndNoRecommStatus(eachRecomm, recommendationStatus, computedChartEachDayValue);
    } else if (recommendationStatus === RECOMMENDATION_STATUS_KEYS.AUTO_APPROVAL_STOP) {
        computedChartEachDayValue = computeGroupCountForEachRecommForAutoApprovalStopStatus(eachRecomm, recommendationStatus, computedChartEachDayValue);
    } else {
        computedChartEachDayValue = computeGroupCountForEachRecommForApprovalStatus(eachRecomm, recommendationStatus, computedChartEachDayValue);
    }
    return computedChartEachDayValue;
};

const computeGroupCountForEachRecommForRecommAndNoRecommStatus = (eachRecomm, recommendationStatus, computedChartEachDayValue) => {
    const computedChartEachDayValues = computedChartEachDayValue;
    eachRecomm[recommendationStatus] && map(eachRecomm[recommendationStatus], eachGroup => {
        if (computedChartEachDayValues[eachGroup.groupName]) {
            computedChartEachDayValues[eachGroup.groupName] += eachGroup[getCountKeyForRecommStatus(recommendationStatus)];
        } else {
            computedChartEachDayValues[eachGroup.groupName] = eachGroup[getCountKeyForRecommStatus(recommendationStatus)];
        }
    });
    return computedChartEachDayValues;
};

const computeGroupCountForEachRecommForAutoApprovalStopStatus = (eachRecomm, recommendationStatus, computedChartEachDayValue) => {
    const computedChartEachDayValues = computedChartEachDayValue;
    if (!isEmpty(eachRecomm[recommendationStatus]) && !isUndefined(eachRecomm[recommendationStatus][0])) {
        map(eachRecomm[recommendationStatus][0].aggregationByReason, eachReason => {
            if (computedChartEachDayValues[eachReason.reason]) {
                computedChartEachDayValues[eachReason.reason] += eachReason[getCountKeyForRecommStatus(recommendationStatus)];
            } else {
                computedChartEachDayValues[eachReason.reason] = eachReason[getCountKeyForRecommStatus(recommendationStatus)];
            }
        });
    }
    return computedChartEachDayValues;
};

const computeGroupCountForEachRecommForApprovalStatus = (eachRecomm, recommendationStatus, computedChartEachDayValue) => {
    const computedChartEachDayValues = computedChartEachDayValue;
    eachRecomm[recommendationStatus] && map(eachRecomm[recommendationStatus], eachAnalyst => {
        if (!isEmpty(eachAnalyst)) {
            if (computedChartEachDayValues[eachAnalyst.status]) {
                computedChartEachDayValues[eachAnalyst.status] += eachAnalyst[getCountKeyForRecommStatus(recommendationStatus)];
            } else {
                computedChartEachDayValues[eachAnalyst.status] = eachAnalyst[getCountKeyForRecommStatus(recommendationStatus)];
            }
        }
    });
    return computedChartEachDayValues;
};

const checkIfWeeklyAndGetWeeklyComputedChartData = (dailyWeeklyViewValue, computedChartData) => {
    let weeklyComputedChartData = [];
    if (DAILY_WEEKLY_TOGGLE_TAB.WEEKLY === dailyWeeklyViewValue) {
        const weeklyComputedChartDataFormatted = getWeeklyComputedChartData(computedChartData);
        weeklyComputedChartData = chain(weeklyComputedChartDataFormatted)
            .sortBy(DASHBOARD_GRAPH_X_AXIS_KEY)
            .value();
    }
    return weeklyComputedChartData;
};

const getWeeklyComputedChartData = (computedChartDataValues) => {
    const weeklyGroupedDataFromDailyComputedChartData = getWeeklyGroupedDataFromDailyComputedChartData(computedChartDataValues);
    const weeklyComputedChartData = [];
    map(weeklyGroupedDataFromDailyComputedChartData, (eachWeek, weekKey) => {
        let tempWeeklyComputedChartData = {[DASHBOARD_GRAPH_X_AXIS_KEY]: weekKey};
        map(eachWeek, eachDailyValueData => {
            map(eachDailyValueData, (value, dailyValueKey) => {
                /* If key is 'date' skip that, as we already have the key 'date' populated from weekKey */
                if (dailyValueKey !== DASHBOARD_GRAPH_X_AXIS_KEY) {
                    if (tempWeeklyComputedChartData[dailyValueKey]) {
                        tempWeeklyComputedChartData[dailyValueKey] += value;
                    } else {
                        tempWeeklyComputedChartData[dailyValueKey] = value;
                    }
                }
            });
        });
        weeklyComputedChartData.push(tempWeeklyComputedChartData);
    });

    return weeklyComputedChartData;
};

/* This function generates the weeklyComputedChartDataGroupedObject in as mentioned in "WEEKLYCOMPUTEDCHARTDATAGROUPEDOBJECT" in constant.js. */
const getWeeklyGroupedDataFromDailyComputedChartData = (computedChartDataValues) => {
    const dailyComputedChartDataValues = computedChartDataValues;
    const weeklyComputedChartDataGroupedObject = {};
    let tempWeeklyComputedChartDataGroupedArray = [];
    const dailyComputedChartDataValuesKeys = keys(dailyComputedChartDataValues);
    let weekStartKey = dailyComputedChartDataValuesKeys[0];
    for (let i = 0; i < dailyComputedChartDataValuesKeys.length; i++) {
        tempWeeklyComputedChartDataGroupedArray.push(dailyComputedChartDataValues[dailyComputedChartDataValuesKeys[i]]);
        if ((i !== 0) && ((i + 1) % 7 === 0)) {
            weeklyComputedChartDataGroupedObject[`${weekStartKey}/${dailyComputedChartDataValuesKeys[i]}`] = tempWeeklyComputedChartDataGroupedArray;
            tempWeeklyComputedChartDataGroupedArray = [];
            weekStartKey = dailyComputedChartDataValuesKeys[i + 1];
        } else {
            if (i === dailyComputedChartDataValuesKeys.length - 1) {
                weeklyComputedChartDataGroupedObject[`${weekStartKey}/${dailyComputedChartDataValuesKeys[i]}`] = tempWeeklyComputedChartDataGroupedArray;
            }
        }
    }
    return weeklyComputedChartDataGroupedObject;
};

const getCountKeyForRecommStatus = recommendationStatus => (recommendationStatus === RECOMMENDATION_STATUS_KEYS.RECOMMENDATION || recommendationStatus === RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION) ? RECOMMENDATION_RESPONSE_COUNT_KEYS.GROUP_COUNT : RECOMMENDATION_RESPONSE_COUNT_KEYS.COUNT;

export const getXAxisTickFormatter = (tick, dailyWeeklyTabValue) => (dailyWeeklyTabValue === DAILY_WEEKLY_TOGGLE_TAB.DAILY) ? DateUtil.formatDate(tick, DateUtil.DATE_FORMATS.DAY_HYPHEN_MONTH_NAME) : DateUtil.formatDate(tick.substring(11, 21), DateUtil.DATE_FORMATS.DAY_HYPHEN_MONTH_NAME);

export const generateGetUrlDashboardSummary = (category, queryFilter, recommCompetitor, isFareChangesSummaryFromAthena) => {
    if (DASHBOARD_TOP_TABS_VALUE.COMPETITION === recommCompetitor) {
        const FARE_CHANGES_SUMMARY_URL = isFareChangesSummaryFromAthena ?
            `${URL_FARE_CHANGES_SUMMARY_ATHENA_FLATTENED}` : `${URL_FARE_CHANGES_SUMMARY_MONGO_FLATTENED}`;
        const query = requestBuilder(queryFilter, PAYLOAD_PARAMS_DASHBOARD_COMPETITION_SUMMARY);
        return isEmpty(query) ? '' : {method: 'POST', url: FARE_CHANGES_SUMMARY_URL, data: query};
    } else {
        const query = queryBuilder(category, queryFilter, PAYLOAD_PARAMS_DASHBOARD_SUMMARY);
        return isEmpty(query) ? '' : {method: 'GET', url: `${URL_RECOMM_SUMMARY_FLATTENED}?${query}`};
    }
};

export const generateUrlForSaveCriteria = (savedCriteriaList, title, data) => {
    let completeUrl = {method: 'POST', url: URL_SAVE_CRITERIA, data: data};
    map(savedCriteriaList, eachValue => {
        if (eachValue.title === title) {
            completeUrl = {method: 'PUT', url: URL_SAVE_CRITERIA, data: data};
        }
    });

    return completeUrl;
}

const computeGridDataForAnalyst = (summaryData, percentAbsolute, reasonsDropdownData) => {

    const flattenedData = chain(summaryData)
        .filter(RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS)
        .map(eachSummaryData =>
            map(eachSummaryData[RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS], analyst => analyst && (
                {
                    ...eachSummaryData,
                    [DateUtil.formatDate(eachSummaryData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE], DateUtil.DATE_FORMATS.DAY_HYPHEN_MONTH_NAME)]: analyst.count,
                    [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_STATUS]: analyst.status,
                    [KEY_ANALYST]: analyst.analyst,
                    actionedDate: DateUtil.formatDate(eachSummaryData[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE], DateUtil.DATE_FORMATS.DAY_HYPHEN_MONTH_NAME)
                }
            )))
        .sortBy(DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE)
        .flatten()
        .value();

    const groupedData = groupByMultipleProperties(without(flattenedData, undefined), [KEY_ANALYST, DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_STATUS]);
    return computeGroupedGridData(groupedData, percentAbsolute, reasonsDropdownData);
};

const computeGroupedGridData = (groupedData, percentAbsolute, reasonsDropdownData) => {
    const groupedDataNew = groupedData;
    const checkedFilterReasons = getCheckedFilterReasons(reasonsDropdownData);
    const ignoredKeys = [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS, 'actionedDate', 'analyst_status'];

    map(groupedDataNew, (eachAnalyst, analystKey) => {
        map(eachAnalyst.subRows, eachStatus => {
            let tempSubRowInsideObject = {};
            eachStatus && map(eachStatus.subRows, eachSummaryDataSubRow => {
                tempSubRowInsideObject = getNewTempSubRowIfPosIsDifferent(tempSubRowInsideObject, eachSummaryDataSubRow);
                let isChecked = [];
                if (eachStatus && DASHBOARD_STRUCTURAL_APPROVAL_STATUS_NO_RECOMMENDATION === eachStatus.status) {
                    map(eachSummaryDataSubRow[RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS], analystData => {
                        analystData && DASHBOARD_STRUCTURAL_APPROVAL_STATUS_NO_RECOMMENDATION === analystData.status &&
                        analystData['reasons'] && map(analystData.reasons, eachInsideGroup => {
                            map(eachInsideGroup.aggregationByReason, eachReason => {
                                isChecked.push(checkedFilterReasons.indexOf(eachReason.reason) > -1);
                            });
                        });
                    });
                }
                if (isEmpty(isChecked) || includes(isChecked, true)) {
                    if (tempSubRowInsideObject[eachSummaryDataSubRow.pos][eachSummaryDataSubRow.actionedDate]) {
                        tempSubRowInsideObject[eachSummaryDataSubRow.pos][eachSummaryDataSubRow.actionedDate] += eachSummaryDataSubRow[eachSummaryDataSubRow.actionedDate];
                    } else {
                        tempSubRowInsideObject[eachSummaryDataSubRow.pos][eachSummaryDataSubRow.actionedDate] = eachSummaryDataSubRow[eachSummaryDataSubRow.actionedDate];
                    }
                }
            });
            eachStatus.subRows = values(tempSubRowInsideObject);
            /* Logic for addition of all Pos counts for each Status. */
            map(eachStatus.subRows, eachSubRow => {
                map(keys(eachSubRow), eachKey => {
                    if (ignoredKeys.indexOf(eachKey) === -1) {
                        if (eachStatus[eachKey]) {
                            eachStatus[eachKey] += eachSubRow[eachKey];
                        } else {
                            eachStatus[eachKey] = eachSubRow[eachKey];
                        }
                        if (eachAnalyst['totalPerAnalyst-' + eachKey]) {
                            eachAnalyst['totalPerAnalyst-' + eachKey] += eachSubRow[eachKey];
                        } else {
                            eachAnalyst['totalPerAnalyst-' + eachKey] = eachSubRow[eachKey];
                        }
                    }
                });
            });
        });

        if (PERCENTAGE_ABSOLUTE_TABS_VALUE.PERCENTAGE === percentAbsolute) {
            map(eachAnalyst.subRows, eachStatus => {
                map(eachStatus.subRows, eachSubRow => {
                    map(keys(eachSubRow), eachKey => {
                        if (ignoredKeys.indexOf(eachKey) === -1) {
                            eachSubRow[eachKey] = (eachSubRow[eachKey] * 100 / eachAnalyst['totalPerAnalyst-' + eachKey]).toFixed(2);
                        }
                    });
                });
                map(keys(eachStatus), eachKey => {
                    if (['analyst_status', 'subRows', 'status'].indexOf(eachKey) === -1) {
                        eachStatus[eachKey] = (eachStatus[eachKey] * 100 / eachAnalyst['totalPerAnalyst-' + eachKey]).toFixed(2);
                    }
                });
            });
        }
    });

    return groupedDataNew;
};

const getCheckedFilterReasons = (reasonsDropdownData) => {
    const reasonsDropdownDataNew = reasonsDropdownData;
    const checkedFilterReasons = [];
    map(reasonsDropdownDataNew, eachGroup => {
        map(eachGroup.children, eachReason => {
            if (eachReason.checked) {
                checkedFilterReasons.push(eachReason.label);
            }
        });
    });

    return checkedFilterReasons;
};

const getNewTempSubRowIfPosIsDifferent = (tempSubRowInsideObject, eachSummaryDataSubRow) => {
    let tempSubRowObject = tempSubRowInsideObject;
    if (!tempSubRowObject[eachSummaryDataSubRow.pos]) {
        tempSubRowObject[eachSummaryDataSubRow.pos] = {
            [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS]: eachSummaryDataSubRow.pos,
            actionedDate: eachSummaryDataSubRow.actionedDate,
            analyst_status: eachSummaryDataSubRow.status
        };
    }

    return tempSubRowObject;
};

export const computeGridData = (filteredSummaryData, recommendationStatus, datesInSequence, filterBy, percentAbsolute, reasonsDropdownData) => {
    let computedGridData = getClonedObject(isEmpty(filterBy) ? datesInSequence : generateDatesBetween(getFromToDate(filterBy), true));
    if (RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS === recommendationStatus) {
        return computeGridDataForAnalyst(filteredSummaryData, percentAbsolute, reasonsDropdownData);
    } else {
        /*Loop thru filteredSummaryData and group eachRecomm by date*/
        map(filteredSummaryData, eachRecomm => {
            const key = DateUtil.formatDate(eachRecomm[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE], DateUtil.DATE_FORMATS.YEAR_MONTH_DAY);
            if (computedGridData[key]) {
                computedGridData[key].push(eachRecomm);
            }
        });
        /*Loop thru already computed computedGridData(grouped by date) and group by region*/
        const computedData = getComputedGridDataGroupedByRegion(computedGridData, recommendationStatus);
        return computedData;
    }
};

const getComputedGridDataGroupedByRegion = (computedGridData, recommendationStatus) => {
    const computedGridDataGroupedByRegion = computedGridData;
    map(computedGridDataGroupedByRegion, (eachDate, dateKey) => {
        const dailyComputedGridDataGroupedByRegionArray = chain(eachDate)
            .groupBy('region')
            .map((value, regionKey) => ({region: regionKey, subRows: value}))
            .value();

        /*Loop thru already computed dailyComputedGridDataGroupedByRegionArray(grouped by region) and group by POS*/
        /*Replace the old computedGridDataGroupedByRegion[dateKey] with newly formed computedGridDataGroupedByPosForRegions*/
        computedGridDataGroupedByRegion[dateKey] = getComputedGridDataGroupedByPosForRegions(dailyComputedGridDataGroupedByRegionArray, recommendationStatus);
    });

    return computedGridDataGroupedByRegion;
};

const getComputedGridDataGroupedByPosForRegions = (dailyComputedGridDataGroupedByRegionArray, recommendationStatus) => {
    const dailyComputedGridDataGroupedByRegion = dailyComputedGridDataGroupedByRegionArray;
    map(dailyComputedGridDataGroupedByRegion, eachRegion => {
        const dailyComputedGridDataGroupedByPosArray = chain(eachRegion.subRows)
            .groupBy('pos')
            .map((value, posKey) => ({pos: posKey, subRows: value}))
            .value();

        /*Loop thru already computed dailyComputedGridDataGroupedByPosArray(grouped by Pos) and group by origin-destination combination*/
        /*Replace the old eachRegion[subRows] with newly formed tempRegionSubRowsArray*/
        eachRegion.subRows = getComputedGridDataGroupedByPoses(dailyComputedGridDataGroupedByPosArray, recommendationStatus);
    });

    return dailyComputedGridDataGroupedByRegion;
};

const getComputedGridDataGroupedByPoses = (dailyComputedGridDataGroupedByPosArray, recommendationStatus) => {
    const dailyComputedGridDataGroupedByPos = dailyComputedGridDataGroupedByPosArray;
    const girdExpectedRegionSubRows = [];
    map(dailyComputedGridDataGroupedByPos, eachPos => {
        let originDestinationKey = '';
        const girdExpectedPosObject = {};
        const gridExpectedSubRows = {};
        let tempSubRowInsideObject = {};
        let totalUniqueCountPerPos = 0;
        let totalImpactedFaresPerPos = 0;
        map(eachPos.subRows, eachSubRow => {
            originDestinationKey = `${eachSubRow.origin}-${eachSubRow.destination}`;
            tempSubRowInsideObject = getNewTempSubRowIfOriginationDestinationCombinationIsDifferent(tempSubRowInsideObject, originDestinationKey, eachSubRow, recommendationStatus);

            /* Logic for Unique Count and Impacted Fares Count. */
            if (eachSubRow.uniqueLaddersAndImpactedFaresCount) {
                let eachSubRowUniqueLaddersCount = eachSubRow.uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_UNIQUE_COUNT_KEYS_MAPPING[recommendationStatus]];
                tempSubRowInsideObject[originDestinationKey].uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_UNIQUE_COUNT_KEYS_MAPPING[recommendationStatus]] += (isUndefined(eachSubRowUniqueLaddersCount) ? 0 : eachSubRowUniqueLaddersCount);
                totalUniqueCountPerPos += (isUndefined(eachSubRowUniqueLaddersCount) ? 0 : eachSubRowUniqueLaddersCount);

                let eachSubRowImpactedFaresCount = eachSubRow.uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_IMPACTED_FARES_KEYS_MAPPING[recommendationStatus]];
                tempSubRowInsideObject[originDestinationKey].uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_IMPACTED_FARES_KEYS_MAPPING[recommendationStatus]] += (isUndefined(eachSubRowImpactedFaresCount) ? 0 : eachSubRowImpactedFaresCount);
                totalImpactedFaresPerPos += (isUndefined(eachSubRowImpactedFaresCount) ? 0 : eachSubRowImpactedFaresCount);
            }
            /* If the count value is 0 or '0', then replace it with empty value '', so that in UI it will be displayed as blank. */
            let tempSubRowUniqueLaddersCount = tempSubRowInsideObject[originDestinationKey].uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_UNIQUE_COUNT_KEYS_MAPPING[recommendationStatus]];
            tempSubRowInsideObject[originDestinationKey].uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_UNIQUE_COUNT_KEYS_MAPPING[recommendationStatus]] = (tempSubRowUniqueLaddersCount === 0 || tempSubRowUniqueLaddersCount === '0') ? '' : tempSubRowUniqueLaddersCount;
            tempSubRowInsideObject[originDestinationKey][UNIQUE_COUNT_KEY] = (tempSubRowUniqueLaddersCount === 0 || tempSubRowUniqueLaddersCount === '0') ? '' : tempSubRowUniqueLaddersCount;

            let tempSubRowImpactedFaresCount = tempSubRowInsideObject[originDestinationKey].uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_IMPACTED_FARES_KEYS_MAPPING[recommendationStatus]];
            tempSubRowInsideObject[originDestinationKey].uniqueLaddersAndImpactedFaresCount[RECOMMENDATION_STATUS_KEYS_IMPACTED_FARES_KEYS_MAPPING[recommendationStatus]] = (tempSubRowImpactedFaresCount === 0 || tempSubRowImpactedFaresCount === '0') ? '' : tempSubRowImpactedFaresCount;
            tempSubRowInsideObject[originDestinationKey][IMPACTED_FARES_COUNT_KEY] = (tempSubRowImpactedFaresCount === 0 || tempSubRowImpactedFaresCount === '0') ? '' : tempSubRowImpactedFaresCount;

            /* Logic for Reasons Count. */
            map(eachSubRow[recommendationStatus], eachGroup => {
                eachGroup.aggregationByReason && map(eachGroup.aggregationByReason, eachReason => {
                    eachReason.reason = checkAndModifyReasonNameIfBaseFareChanged(eachReason, eachGroup);
                    if (tempSubRowInsideObject[originDestinationKey][eachReason.reason]) {
                        tempSubRowInsideObject[originDestinationKey][eachReason.reason] += eachReason[RECOMMENDATION_RESPONSE_COUNT_KEYS.COUNT];
                    } else {
                        tempSubRowInsideObject[originDestinationKey][eachReason.reason] = eachReason[RECOMMENDATION_RESPONSE_COUNT_KEYS.COUNT];
                    }

                    if (girdExpectedPosObject[eachReason.reason]) {
                        girdExpectedPosObject[eachReason.reason] += eachReason[RECOMMENDATION_RESPONSE_COUNT_KEYS.COUNT];
                    } else {
                        girdExpectedPosObject[eachReason.reason] = eachReason[RECOMMENDATION_RESPONSE_COUNT_KEYS.COUNT];
                    }
                });
            });
            if (!gridExpectedSubRows[originDestinationKey]) {
                gridExpectedSubRows[originDestinationKey] = tempSubRowInsideObject[originDestinationKey];
            }

        });
        /*After each pos - all subrows - calculations are done, set the girdExpectedPosObject and push the  gridExpectedSubRows to girdExpectedPosObject*/
        girdExpectedPosObject.pos = eachPos.pos;
        girdExpectedPosObject[UNIQUE_COUNT_KEY] = totalUniqueCountPerPos === 0 ? '' : totalUniqueCountPerPos;
        girdExpectedPosObject[IMPACTED_FARES_COUNT_KEY] = totalImpactedFaresPerPos === 0 ? '' : totalImpactedFaresPerPos;
        girdExpectedPosObject.subRows = values(gridExpectedSubRows);
        girdExpectedRegionSubRows.push(girdExpectedPosObject);
    });

    return girdExpectedRegionSubRows;
};

const getNewTempSubRowIfOriginationDestinationCombinationIsDifferent = (tempSubRowInsideObject, originDestinationKey, eachSubRow, recommendationStatus) => {
    let tempSubRowObject = tempSubRowInsideObject;
    if (!tempSubRowObject[originDestinationKey]) {
        tempSubRowObject[originDestinationKey] = {
            origin: eachSubRow.origin,
            destination: eachSubRow.destination,
            uniqueLaddersAndImpactedFaresCount: {
                [RECOMMENDATION_STATUS_KEYS_UNIQUE_COUNT_KEYS_MAPPING[recommendationStatus]]: 0,
                [RECOMMENDATION_STATUS_KEYS_IMPACTED_FARES_KEYS_MAPPING[recommendationStatus]]: 0
            }
        };
    }

    return tempSubRowObject;
};

/* If reason name is "Base fare changed", we need to modify(append it with Competitor/Host) as in the response from backend we are getting it as same name for both the group(COMPETITOR_OTHER_FARE_COMPONENT_CHANGE and HOST_OTHER_FARE_COMPONENT_CHANGE) */
const checkAndModifyReasonNameIfBaseFareChanged = (eachReason, eachGroup) => {
    let eachReasonName = eachReason.reason;
    if ('Base fare changed' === eachReason.reason && DASHBOARD_TACTICAL_COMPETITOR_OTHER_FARE_COMPONENT_CHANGE === eachGroup.groupName) {
        eachReasonName = 'Competitor ' + eachReason.reason;
    } else if ('Base fare changed' === eachReason.reason && DASHBOARD_TACTICAL_HOST_OTHER_FARE_COMPONENT_CHANGE === eachGroup.groupName) {
        eachReasonName = 'Host ' + eachReason.reason;
    }

    return eachReasonName;
};

export const getFromToDate = fromToDate => {
    if (isEmpty(fromToDate) || isEmpty(fromToDate[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE])) {
        return {
            [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE]: {
                [AGGREGATION_DATES.AGGREGATION_FROM_DATE]: '',
                [AGGREGATION_DATES.AGGREGATION_TO_DATE]: ''
            }
        };
    }

    return {
        [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE]: {
            [AGGREGATION_DATES.AGGREGATION_FROM_DATE]: fromToDate[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE][AGGREGATION_DATES.AGGREGATION_FROM_DATE],
            [AGGREGATION_DATES.AGGREGATION_TO_DATE]: fromToDate[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE][AGGREGATION_DATES.AGGREGATION_TO_DATE]
        }
    }
};

export const displayFromToDate = (fromToDate, dateFormat = '', isShowLabel = false) => {
    const dates = getFromToDate(fromToDate);
    let labelStart = '';
    let labelEnd = '';
    if(isShowLabel) {
        labelStart = 'From: ';
        labelEnd = 'To: ';
    }
    const tempDate = dates[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE];

    return `${labelStart}${DateUtil.formatDate(tempDate[AGGREGATION_DATES.AGGREGATION_FROM_DATE], dateFormat)} - ${labelEnd}${DateUtil.formatDate(tempDate[AGGREGATION_DATES.AGGREGATION_TO_DATE], dateFormat)}`;
};

export const getReasonsDropdownFilteredList = (recommendationStatus, recommendationSummaryData) => {
    const reasonsDropdownInitialData = [];
    const groupNameAndReasons = getGroupNameAndReasonsFromSummaryData(recommendationStatus, recommendationSummaryData, false);
    map(groupNameAndReasons, (value, eachGroup) => {
        const childrenArray = [];
        map(value, eachReason => {
            childrenArray.push({
                label: eachReason,
                value: eachReason,
                checked: true,
                isGroupName: false
            });
        });
        const reasonsDropdownEntry = {
            label: eachGroup,
            value: eachGroup,
            checked: true,
            isGroupName: true,
            children: childrenArray || [],
            childrenForComputation: childrenArray || []
        };
        reasonsDropdownInitialData.push(reasonsDropdownEntry);
    });

    return reasonsDropdownInitialData;
};

export const getGroupNameAndReasonsFromSummaryData = (recommendationStatus, recommendationSummaryData, isGroupNameRequired) => {
    const groupNameAndReasons = {};
    map(recommendationSummaryData, eachRecomm => {
        eachRecomm[recommendationStatus] && map(eachRecomm[recommendationStatus], eachGroup => {
            if (recommendationStatus === RECOMMENDATION_STATUS_KEYS.RECOMMENDATION || recommendationStatus === RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION) {
                eachGroup && map(eachGroup.aggregationByReason, eachReason => {
                    eachReason.reason = checkAndModifyReasonNameIfBaseFareChanged(eachReason, eachGroup);
                    if (groupNameAndReasons[eachGroup.groupName]) {
                        if (groupNameAndReasons[eachGroup.groupName].indexOf(eachReason.reason) === -1) {
                            groupNameAndReasons[eachGroup.groupName].push(eachReason.reason);
                        }
                    } else {
                        groupNameAndReasons[eachGroup.groupName] = [eachReason.reason];
                    }
                });
            } else if (recommendationStatus === RECOMMENDATION_STATUS_KEYS.AUTO_APPROVAL_STOP) {
                if (!isEmpty(eachRecomm[recommendationStatus]) && !isUndefined(eachRecomm[recommendationStatus][0])) {
                    map(eachRecomm[recommendationStatus][0].aggregationByReason, eachReason => {
                        groupNameAndReasons[eachReason.reason] = [];
                    });
                }
            } else if (eachGroup && recommendationStatus === RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS) {
                if (isGroupNameRequired) {
                    groupNameAndReasons[eachGroup.status] = [];
                } else {
                    if (DASHBOARD_STRUCTURAL_APPROVAL_STATUS_NO_RECOMMENDATION === eachGroup.status) {
                        map(eachGroup.reasons, eachInsideGroup => {
                            map(eachInsideGroup.aggregationByReason, eachReason => {
                                if (groupNameAndReasons[eachInsideGroup.groupName]) {
                                    if (groupNameAndReasons[eachInsideGroup.groupName].indexOf(eachReason.reason) === -1) {
                                        groupNameAndReasons[eachInsideGroup.groupName].push(eachReason.reason);
                                    }
                                } else {
                                    groupNameAndReasons[eachInsideGroup.groupName] = [eachReason.reason];
                                }
                            });
                        });
                    }
                }
            }
        });
    });

    const groupNameAndReasonsSortedByGroupName = {};
    forEach(sortBy(keys(groupNameAndReasons)), eachGroup => {
        groupNameAndReasonsSortedByGroupName[eachGroup] = sortBy(groupNameAndReasons[eachGroup]);
    });

    return groupNameAndReasonsSortedByGroupName;
};

export const setAndGetReasonsFilterChangeData = (reasonsDropdownData, currentNode, selectedNodes) => {
    const reasonsDropdownDataNew = getClonedObject(reasonsDropdownData);
    map(reasonsDropdownDataNew, eachGroup => {
        if (currentNode.isGroupName && eachGroup.label === currentNode.label) {
            eachGroup.checked = currentNode.checked;
            map(eachGroup.children, eachReason => {
                eachReason.checked = currentNode.checked;
            });
        } else {
            map(eachGroup.children, eachReason => {
                if (eachReason.label === currentNode.label) {
                    eachReason.checked = currentNode.checked;
                }
            });
        }

        /* If all the children are checked for a group, then mark the group also as checked.
            If any of the children are unchecked for a group, then mark the group also as unchecked. */
        if (!isEmpty(eachGroup.children) && !isUndefined(eachGroup.children)) {
            includes(map(eachGroup.children, 'checked'), false) ? eachGroup.checked = false : eachGroup.checked = true;
        }
    });

    return reasonsDropdownDataNew;
};

export const getPlotBarsConfigByRecommStatus = (recommendationStatus, recommendationSummaryData) => {
    const groupNameAndReasons = getGroupNameAndReasonsFromSummaryData(recommendationStatus, recommendationSummaryData, true);
    return keys(groupNameAndReasons);
};

export const isSelectedApprovalStatus = recommendationStatus => recommendationStatus === RECOMMENDATION_STATUS_KEYS.APPROVAL_STATUS;
